'use client'

// Sign in — the onboarding shell (matches the /create design). OAuth pills,
// password / magic-link tabs, lit-underline fields, the primary capsule CTA.
// Flows unchanged: password, magic link, OAuth.

import React, { Suspense, useEffect, useState } from 'react'
import { useSearchParams } from 'next/navigation'
import { createClient } from '../../lib/supabase/client'
import { siteUrl } from '../../lib/site-url'
import { markArrival } from '../../lib/utm'
import OAuthButtons from '../../components/OAuthButtons'
import InAppBrowserNotice from '../../components/InAppBrowserNotice'
import InAppHint from '../../components/InAppHint'
import EmailCodeEntry from '../../components/EmailCodeEntry'
import AuthShell, { AuthHeading, PasswordField, authAtoms as a } from '../../components/AuthShell'
import { Field, PillButton } from '../../components/ui'
import DoorMark from '../../components/brand/DoorMark'
import { NETWORK_LINE, friendlyAuthError, friendlyThrown, isNetworkError } from '../../lib/auth-errors'
import { safeNext } from '../../lib/auth/safeNext'
import { authFailReason } from '../../lib/auth-reason'
import { trackFunnel, EVENTS } from '../../lib/analytics'

export default function LoginPage() {
  return (
    <Suspense fallback={null}>
      <LoginInner />
    </Suspense>
  )
}

function LoginInner() {
  const searchParams = useSearchParams()
  const next = safeNext(searchParams.get('next'))
  const errorParam = searchParams.get('error')
  // When auth is opened FROM the desktop app it arrives with ?source=app. We
  // then route the finished session through /auth/handoff, which deep-links it
  // back into the app (innernet://). Plain web sign-in goes straight to `next`.
  const fromApp = searchParams.get('source') === 'app'
  const dest = fromApp ? '/auth/handoff' : next
  const signupHref = fromApp ? '/signup?source=app' : '/signup'

  const [tab, setTab] = useState<'password' | 'magic'>('password')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState(friendlyAuthError(errorParam))
  const [success, setSuccess] = useState('')
  const [loading, setLoading] = useState(false)
  // the email is out — offer BOTH doors it contains: the link (any browser)
  // and the code, typed right here (this browser, wherever "here" is).
  const [magicSent, setMagicSent] = useState(false)

  // `npx innernet` opens a browser at /api/cli/pair, which bounces an
  // unauthenticated person here carrying the pair route as `next`. That param
  // is the only evidence the terminal sent them, and attribution strips query
  // strings — so the arrival is recorded here, while the evidence still exists.
  useEffect(() => {
    if (next.startsWith('/api/cli/pair')) markArrival('cli_first', 'npx innernet')
  }, [next])

  // A bounced callback lands back here as ?error=… — the expired magic link, the
  // already-used one. It is a real drop-off with no click behind it, so nothing
  // else in the funnel would ever record it.
  useEffect(() => {
    if (errorParam) trackFunnel(EVENTS.auth_failed, { method: 'callback', reason: authFailReason(errorParam) })
  }, [errorParam])

  const oauthFailed = (msg: string) => {
    // the buttons' own sentences pass through; the browser's "Failed to fetch" does not
    setError(isNetworkError(msg) ? NETWORK_LINE : msg)
    trackFunnel(EVENTS.auth_failed, { method: 'oauth', reason: authFailReason(msg), intent: 'signin' })
  }

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true); setError(''); setSuccess('')
    try {
      const supabase = createClient()
      const { error: err } = await supabase.auth.signInWithPassword({ email, password })
      if (err) {
        setError(friendlyAuthError(err.message))
        // only the failure fires here — the success is AnalyticsIdentity's job
        // (it watches for the real session), so `signed_in` can never double-count.
        trackFunnel(EVENTS.auth_failed, { method: 'password', reason: authFailReason(err.message) })
        setLoading(false); return
      }
      window.location.href = dest
    } catch (err) {
      setError(friendlyThrown(err))
      trackFunnel(EVENTS.auth_failed, { method: 'password', reason: authFailReason(String(err)) })
      setLoading(false)
    }
  }

  const submitMagic = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) {
      setError('enter your email first.')
      trackFunnel(EVENTS.error_shown, { reason: 'email_missing', method: 'magic_link' })
      return
    }
    setLoading(true); setError(''); setSuccess('')
    try {
      const supabase = createClient()
      // /auth/land is a CLIENT page — magic links can arrive with the session
      // in the URL hash, which a server route can never see.
      const { error: err } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: `${siteUrl()}/auth/land?next=${encodeURIComponent(dest)}` },
      })
      if (err) {
        setError(friendlyAuthError(err.message))
        trackFunnel(EVENTS.auth_failed, { method: 'magic_link', reason: authFailReason(err.message), intent: 'signin' })
        setLoading(false); return
      }
      setSuccess('check your email. click the link, or type the code below.')
      setMagicSent(true)
      // credential submitted, session not yet real — the returning-visitor twin
      // of the signup magic link, marked by `intent` so the two stay separable.
      trackFunnel(EVENTS.signup_started, { method: 'magic_link', intent: 'signin' })
    } catch (err) {
      setError(friendlyThrown(err))
      trackFunnel(EVENTS.auth_failed, { method: 'magic_link', reason: authFailReason(String(err)), intent: 'signin' })
    }
    setLoading(false)
  }

  return (
    <AuthShell suffix="sign in" heading={<AuthHeading lead="your memory is waiting," accent="welcome back." />}>
      <InAppBrowserNotice />
      <OAuthButtons next={dest} verb="start with" layout="row" onError={oauthFailed} />
      <InAppHint />

      <div className="auth-or"><span>or with your email</span></div>

      <div className="auth-modes" role="group" aria-label="how to sign in">
        {(['password', 'magic'] as const).map((t) => (
          <button
            key={t}
            type="button"
            className="chip"
            data-on={tab === t}
            onClick={() => { setTab(t); setError(''); setSuccess('') }}
          >
            {t === 'password' ? 'with a password' : 'with a magic link'}
          </button>
        ))}
      </div>

      {error ? <p style={a.err}>{error}</p> : null}
      {success ? <p style={a.ok}>{success}</p> : null}

      {tab === 'password' && (
        <form key="pw" onSubmit={submitPassword} className="view-fade auth-form">
          <Field label="your email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            required autoComplete="email" placeholder="you@example.com" />
          {/* the reveal lives inside the field (law_auth_breathing_ground, 2026-09-09);
              "forgot" rides the label's own line, beside the thing it's about */}
          <PasswordField label="password" value={password} onChange={(e) => setPassword(e.target.value)}
            required autoComplete="current-password" placeholder="your password"
            aside={<a href="/forgot-password" className="auth-aside-link">forgot password?</a>} />
          <PillButton type="submit" className="cta-pill auth-door btn-pill--solid" loading={loading} icon={<DoorMark />}>
            {loading ? 'entering…' : 'enter'}
          </PillButton>
        </form>
      )}

      {tab === 'magic' && !magicSent && (
        <form key="magic" onSubmit={submitMagic} className="view-fade auth-form">
          <Field label="your email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            required autoComplete="email" placeholder="you@example.com" autoFocus />
          <PillButton type="submit" className="cta-pill auth-door btn-pill--solid" loading={loading} icon={<DoorMark />}>
            {loading ? 'sending…' : 'send the link'}
          </PillButton>
          <p style={{ ...a.quiet }}>the email carries a one-tap link and a short code. either one signs you in.</p>
        </form>
      )}

      {tab === 'magic' && magicSent && (
        <div className="view-fade" style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
          {/* "use a different address" and "send it again" live in the code entry itself,
              so this state is never a dead end on any surface that sends the email */}
          <EmailCodeEntry email={email} next={dest} onChangeAddress={() => { setMagicSent(false); setSuccess('') }} />
        </div>
      )}

      <p className="auth-foot">
        new here? <a href={signupHref}>begin your innernet</a>
      </p>
    </AuthShell>
  )
}
